import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  vendors: 'Vendors',
  assessments: 'Assessments',
  dashboards: 'Dashboards',
  compliance: 'Compliance',
  controls: 'Controls',
  evidence: 'Evidence',
  exceptions: 'Exceptions',
  readiness: 'Readiness',
  'risk-acceptances': 'Vendor Risk Register',
  'org-risk-acceptances': 'Risk Register',
  'my-assignments': 'My Tasks',
  settings: 'Settings',
  sso: 'Single Sign-On',
  saml: 'SAML',
  oidc: 'OIDC',
  profile: 'Profile',
  team: 'Users & Permissions',
  branding: 'Custom Branding',
  smtp: 'SMTP',
  integrations: 'Integrations',
  webhooks: 'Webhooks',
  notifications: 'Notifications',
  'escalation-contacts': 'Escalation Contacts',
  'audit-log': 'Activity Log',
  billing: 'Billing & Plans',
  new: 'New',
};

function getLabel(segment: string): string {
  if (/^[0-9a-f-]{36}$/i.test(segment)) return 'Details';
  return ROUTE_LABELS[segment] ?? segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Full-path breadcrumb overrides — for a page whose crumb trail should NOT follow its URL
// segments because it lives under a different nav section than its (legacy/stable) path implies.
// Keyed by the EXACT pathname, so sibling paths (e.g. /dashboards/other) are unaffected.
const PATH_OVERRIDES: Record<string, { label: string; href: string }[]> = {
  // Compliance Dashboard relocated to the Compliance Program section; route stays /dashboards/compliance.
  '/dashboards/compliance': [
    { label: 'Compliance Program', href: '/compliance' },
    { label: 'Compliance Dashboard', href: '/dashboards/compliance' },
  ],
};

// PREFIX overrides — a section re-homed under another nav group, whose whole subtree keeps its (stable) path: the
// prefix's own crumbs replace its first segment, and any deeper segments (/new, /:id) follow as usual.
// ⚠ RET-10: the Risk Register (org risk responses) lives under Compliance Program; route stays /org-risk-acceptances.
const PREFIX_OVERRIDES: { prefix: string; crumbs: { label: string; href: string }[] }[] = [
  {
    prefix: '/org-risk-acceptances',
    crumbs: [
      { label: 'Compliance Program', href: '/compliance' },
      { label: 'Risk Register', href: '/org-risk-acceptances' },
    ],
  },
];

export function Breadcrumbs() {
  const { pathname } = useLocation();

  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const segmentCrumbs = (from: number) => segments.slice(from).map((seg, i) => {
    const href = '/' + segments.slice(0, from + i + 1).join('/');
    return { label: getLabel(seg), href };
  });
  const prefixed = PREFIX_OVERRIDES.find(
    (o) => pathname === o.prefix || pathname.startsWith(o.prefix + '/'),
  );
  const crumbs = PATH_OVERRIDES[pathname]
    ?? (prefixed
      ? [...prefixed.crumbs, ...segmentCrumbs(prefixed.prefix.split('/').filter(Boolean).length)]
      : segmentCrumbs(0));

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={crumb.href} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-text-secondary" />}
            {isLast ? (
              <span className="font-medium text-text-primary">{crumb.label}</span>
            ) : (
              <Link
                to={crumb.href}
                className={cn(
                  'text-text-secondary transition-colors hover:text-text-primary',
                )}
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
